import { getLanguages } from '../../../../utils/language';
import { readFile } from '../../../../utils';
import { dayjs } from '@koob/dates';

function formatApiTime(time) {
  if (time) {
    const timeArray = time.split(':');

    return {
      hour: timeArray[0],
      min: timeArray[1],
    };
  }
  return null;
}

function getTimeCombined(hours, minutes) {
  if (!hours) {
    return;
  }
  let completeTime = hours + ':';
  if (!minutes) {
    completeTime += '00';
  } else {
    completeTime += ('' + minutes).padStart(2, '0');
  }
  return completeTime;
}

export function formatExperienceApiData(experienceBdd, deleteIds = false) {
  const periods = experienceBdd?.periods ?? [];
  const dayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const formattedPeriods = [];
  periods
    ?.sort((a, b) => (dayjs(a.startAt).isBefore(dayjs(b.startAt)) ? -1 : 1))
    .forEach((item) => {
      const daysObj = !item.availableDays
        ? []
        : item.availableDays.reduce((obj, day, index) => {
            obj[dayLabels[index]] = day === 1;
            return obj;
          }, {});
      // Legacy periods can carry a flat price next to their per pax grids.
      // The pricing schema rejects it, and that schema is checked from every
      // step, so the stale value would lock the wizard until the pricing step
      // gets mounted and clears it.
      const isPerPax = item.perPax === true || item.perPax === 'true';
      const formattedPeriod = {
        ...item,
        price: isPerPax ? '' : item.price,
        days: JSON.parse(JSON.stringify(daysObj)),
      };
      if (deleteIds) {
        delete formattedPeriod.id;
      }
      formattedPeriods.push(formattedPeriod);
    });

  const languages = getLanguages();

  const obj = {
    ...experienceBdd,
    bestMatches:
      experienceBdd?.bestMatches?.map((match) => {
        return {
          label: match?.name,
          value: match?.id,
        };
      }) ?? undefined,
    incompatibilities:
      experienceBdd?.incompatibilities?.map((match) => {
        return {
          label: match?.name,
          value: match?.id,
        };
      }) ?? undefined,
    koobReference: experienceBdd.id,
    inCountry: experienceBdd?.inCity?.country,
    periods: formattedPeriods,
    mediaLinks: experienceBdd?.mediaLinks ?? [],
    pictures: experienceBdd?.pictures ?? [],
    guidingLanguages: experienceBdd?.guidingLanguages?.map((l) => {
      return {
        value: l,
        label: languages.find((lang) => lang.value === l)?.label,
      };
    }),
    activity: {
      ...experienceBdd?.activity,
      pickUpPointTimeHour: formatApiTime(
        experienceBdd?.activity?.pickUpPointTime,
      )?.hour,
      pickUpPointTimeMin: formatApiTime(
        experienceBdd?.activity?.pickUpPointTime,
      )?.min,
      dropOffPointTimeHour: formatApiTime(
        experienceBdd?.activity?.dropOffPointTime,
      )?.hour,
      dropOffPointTimeMin: formatApiTime(
        experienceBdd?.activity?.dropOffPointTime,
      )?.min,
    },
    transfer: {
      ...experienceBdd?.transfer,
      departureTimeHour: formatApiTime(experienceBdd?.transfer?.departureTime)
        ?.hour,
      departureTimeMin: formatApiTime(experienceBdd?.transfer?.departureTime)
        ?.min,
      arrivalTimeHour: formatApiTime(experienceBdd?.transfer?.arrivalTime)
        ?.hour,
      arrivalTimeMin: formatApiTime(experienceBdd?.transfer?.arrivalTime)?.min,
    },
    programs: (experienceBdd?.programs ?? []).map((program) => {
      return {
        ...program,
        startingTimeHour: formatApiTime(program?.startingTime)?.hour,
        startingTimeMin: formatApiTime(program?.startingTime)?.min,
        endingTimeHour: formatApiTime(program?.endingTime)?.hour,
        endingTimeMin: formatApiTime(program?.endingTime)?.min,
        meals: (program?.meals ?? []).map((meal) => {
          return {
            label: meal.charAt(0).toUpperCase() + meal.slice(1),
            value: meal,
          };
        }),
      };
    }),
    accommodationCategories: (experienceBdd?.accommodationCategories ?? []).map(
      (cat) => ({
        ...cat,
        id: deleteIds ? undefined : cat.id,
        prices: (cat.prices ?? []).map((price) => {
          // Resolve roomIndex from accommodationRoomId for grid display
          let roomIndex = price.roomIndex;
          if (roomIndex == null && price.accommodationRoomId) {
            roomIndex = (experienceBdd?.accommodationRooms ?? []).findIndex(
              (r) => r.id === price.accommodationRoomId,
            );
            if (roomIndex === -1) roomIndex = undefined;
          }
          return {
            ...price,
            roomIndex,
            id: deleteIds ? undefined : price.id,
          };
        }),
      }),
    ),
    accommodationRooms: (experienceBdd?.accommodationRooms ?? []).map(
      (room) => ({
        ...room,
        id: deleteIds ? undefined : room.id,
      }),
    ),
  };

  if (deleteIds) {
    delete obj.id;
    delete obj?.transfer?.id;
    delete obj?.activity?.id;
  }

  return obj;
}

export async function formatExperienceFormPayload(experience) {
  const formattedPeriods = [];
  const formatPrograms = async (programs) => {
    return await Promise.all(
      programs.map(async (program) => {
        return {
          ...program,
          programExperiences: program?.programExperiences?.map((programExp) => {
            return {
              id: programExp?.id.toString(),
            };
          }),
          startingTime: getTimeCombined(
            program?.startingTimeHour,
            program?.startingTimeMin,
          ),
          endingTime: getTimeCombined(
            program?.endingTimeHour,
            program?.endingTimeMin,
          ),
          guiding: experience?.guiding ?? program?.guiding,
          guidingLanguages: experience?.guidingLanguages?.reduce(
            (acc, l) => [...acc, l.value],
            [],
          ),
          meals: program?.meals?.map((meal) => meal.value ?? meal),
          accomodationExperience:
            program.accomodationType === 'nightTransfer'
              ? program.accomodationExperience
              : undefined,
          accomodationHotel:
            program.accomodationType === 'hotel'
              ? program.accomodationHotel
              : undefined,
          alternativeHotels:
            program.accomodationType === 'hotel'
              ? program.alternativeHotels
              : undefined,
          picture: program.picture
            ? await (async () => {
                const pic = program.picture;
                const fileCandidate = pic?.file ?? pic;
                let imageUrl;
                let displayName = pic?.displayName ?? pic?.name;
                let id = pic?.id;

                // If we have an id, this is an existing picture coming from API
                if (id) {
                  imageUrl = pic?.imageUrl ?? pic?.url;
                } else if (
                  typeof fileCandidate !== 'string' &&
                  fileCandidate &&
                  fileCandidate instanceof Blob
                ) {
                  // New upload: we got a File/Blob
                  imageUrl = await readFile(fileCandidate);
                  displayName = displayName ?? fileCandidate.name;
                } else {
                  // Fallbacks: if it's a string or plain object with url/imageUrl
                  imageUrl =
                    (typeof pic === 'string' ? pic : undefined) ??
                    pic?.imageUrl ??
                    pic?.url ??
                    undefined;
                }

                return {
                  imageUrl,
                  displayName,
                  isMainPicture: false,
                  id,
                };
              })()
            : null,
        };
      }),
    );
  };

  if (experience?.periods) {
    experience?.periods.forEach((period) => {
      const days = Object.values(period.days ?? period.availableDays).map(
        (val) => (val ? 1 : 0),
      );

      formattedPeriods.push({
        ...period,
        extras: undefined,
        availableDays: days,
        price: period.price === '' ? null : period.price,
        autoConfirmDays:
          period.autoConfirmDays === '' ? null : period.autoConfirmDays,
      });
    });
  }

  const programs = await formatPrograms(experience?.programs ?? []);

  return {
    ...experience,
    isRepeatable: experience?.isRepeatable ?? false,
    bestMatches:
      experience?.bestMatches?.map((match) => {
        return {
          label: match?.label,
          id: match?.value ?? match?.id,
        };
      }) ?? undefined,
    incompatibilities:
      experience?.incompatibilities?.map((match) => {
        return {
          label: match?.label,
          id: match?.value ?? match?.id,
        };
      }) ?? undefined,
    distance:
      experience?.distance && experience?.distance !== ''
        ? experience.distance
        : undefined,
    periods: formattedPeriods,
    mainPicture: experience.mainPicture
      ? {
          displayName: experience.mainPicture.displayName,
          imageUrl: experience.mainPicture.imageUrl,
        }
      : undefined,
    pictures: experience.pictures.map((elem) => {
      return {
        displayName: elem.displayName,
        imageUrl: elem.imageUrl,
        isMainPicture: elem.isMainPicture,
        id: elem.id,
      };
    }),
    guidingLanguages: experience?.guidingLanguages?.map((l) => {
      return l.value ?? l;
    }),
    activity:
      experience.type !== 'Activity'
        ? undefined
        : {
            ...experience.activity,
            pickUpPointTime: getTimeCombined(
              experience?.activity?.pickUpPointTimeHour,
              experience?.activity?.pickUpPointTimeMin,
            ),
            dropOffPointTime: getTimeCombined(
              experience?.activity?.dropOffPointTimeHour,
              experience?.activity?.dropOffPointTimeMin,
            ),
            guiding: experience?.guiding ?? experience?.activity?.guiding,
            guidingLanguages: experience?.guidingLanguages?.reduce(
              (acc, l) => [...acc, l.value],
              [],
            ),
          },
    transfer:
      experience.type !== 'Transfer'
        ? undefined
        : {
            ...experience.transfer,
            boat:
              experience.transfer?.type !== 'BOAT'
                ? undefined
                : experience.transfer.boat,
            air:
              experience.transfer?.type !== 'AIR'
                ? undefined
                : experience.transfer.air,
            land:
              experience.transfer?.type !== 'LAND'
                ? undefined
                : experience.transfer.land,
            rail:
              experience.transfer?.type !== 'RAIL'
                ? undefined
                : experience.transfer.rail,
            departureTime: getTimeCombined(
              experience?.transfer?.departureTimeHour,
              experience?.transfer?.departureTimeMin,
            ),
            arrivalTime: getTimeCombined(
              experience?.transfer?.arrivalTimeHour,
              experience?.transfer?.arrivalTimeMin,
            ),
            guiding: experience?.guiding ?? experience?.transfer?.guiding,
            guidingLanguages: experience?.guidingLanguages?.reduce(
              (acc, l) => [...acc, l.value],
              [],
            ),
          },
    programs: programs,
    accommodationCategories: (experience?.accommodationCategories ?? []).map(
      (cat) => ({
        ...cat,
        prices: (cat.prices ?? []).map((price) => ({
          ...price,
          accommodationRoom: price.accommodationRoomId
            ? price.accommodationRoomId
            : price.roomIndex != null
              ? (experience?.accommodationRooms?.[price.roomIndex]?.id ??
                experience?.accommodationRooms?.[price.roomIndex])
              : undefined,
        })),
      }),
    ),
    accommodationRooms: experience?.accommodationRooms ?? [],
  };
}

export const formatPromotionFormPayload = (promotion) => {
  const formattedAvailablePeriods = [];
  const promotionCopy = { ...promotion };
  if (promotionCopy?.periods) {
    promotionCopy?.periods.forEach((period) => {
      const days = Object.values(period.days || {}).reduce(
        (acc, val, index) => {
          if (val) {
            acc.push(index + 1);
          }
          return acc;
        },
        [],
      );

      formattedAvailablePeriods.push({
        startAt: period.startAt,
        endAt: period.endAt,
        days: days,
        paxRanges: promotionCopy?.benefit?.promoPerRoom
          ? []
          : (period?.paxRanges || []).map((item) => ({
              ...item,
              value: item.price,
              price: undefined,
            })),
        ageRanges: promotionCopy?.benefit?.promoPerRoom
          ? []
          : (period?.ageRanges || []).map((item) => ({
              ...item,
              value: item.price,
              price: undefined,
            })),
      });
    });
  }

  delete promotionCopy?.params;
  delete promotionCopy?.periods;

  const obj = {
    ...promotionCopy,
    linkedPromotions: promotionCopy?.cumulativePromotions,
    availablePeriods: formattedAvailablePeriods,
    benefit: promotionCopy?.benefit
      ? {
          ...promotionCopy?.benefit,
          kind: promotionCopy?.benefit?.kind?.value,
          value:
            promotionCopy?.benefit?.value !== undefined &&
            promotionCopy?.benefit?.value !== null
              ? Number(promotionCopy?.benefit?.value)
              : null,
        }
      : null,
  };

  return obj;
};

export const formatPromotionApiData = (promotion) => {
  return {
    ...promotion,
    cumulativePromotions: promotion?.linkedPromotions,
    benefit: {
      ...promotion?.benefit,
    },
  };
};

const periodsOverlap = (period1, period2) => {
  const period1StartAt = new Date(period1.startAt);
  const period1EndAt = new Date(period1.endAt);
  const period2StartAt = new Date(period2.startAt);
  const period2EndAt = new Date(period2.endAt);

  return period1StartAt <= period2EndAt && period1EndAt >= period2StartAt;
};

export const arePromotionPeriodsValid = ({ promotion, experience }) => {
  const validExperiencePeriods = [];

  for (const promotionPeriod of promotion.periods ?? []) {
    const validExperiencePeriod = {
      ...experience,
      periods: experience.periods.filter((experiencePeriod) =>
        periodsOverlap(experiencePeriod, promotionPeriod),
      ),
    };

    if (validExperiencePeriod?.periods?.length > 0) {
      validExperiencePeriods.push(validExperiencePeriod);
    }
  }

  return validExperiencePeriods.length === promotion.periods.length;
};
